/**
 * Client Modal Component
 * Modal for creating and editing clients
 */

import React, { useState, useEffect } from "react";
import { Icon } from "@/components/ui";
import styles from "./ClientModal.module.css";

interface Client {
  id: string;
  nif: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
  status: "active" | "inactive";
  /** Contraseña temporal — sólo requerida al crear */
  password?: string;
}

interface ClientModalProps {
  isOpen: boolean;
  client: Client | null;
  provinces: Array<{ id: number; name: string }>;
  cities: Array<{ id: number; name: string; province: number }>;
  onClose: () => void;
  onSave: (client: Partial<Client>) => void;
}

/**
 * ClientModal component
 */
export function ClientModal({
  isOpen,
  client,
  provinces,
  cities,
  onClose,
  onSave,
}: ClientModalProps) {
  const getInitialData = () => {
    if (client) {
      return {
        nif: client.nif,
        name: client.name,
        contactPerson: client.contactPerson,
        email: client.email,
        phone: client.phone,
        address: client.address,
        // client.city ya es el ID numérico en string (la API devuelve el PK)
        city: client.city ?? "",
        province: client.province,
        postalCode: client.postalCode,
      };
    }
    return {
      nif: "",
      name: "",
      contactPerson: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      province: "",
      postalCode: "",
      password: "",
    };
  };

  const [formData, setFormData] = useState(getInitialData);

  useEffect(() => {
    if (isOpen) {
      setFormData(getInitialData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{client ? "Editar Cliente" : "Nuevo Cliente"}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <Icon name="xCircle" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="nif">CIF / NIF *</label>
              <input
                type="text"
                id="nif"
                name="nif"
                value={formData.nif}
                onChange={handleChange}
                required
                placeholder="B-12345678"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="name">Nombre del Cliente *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Hotel Míramar"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="contactPerson">Persona de Contacto *</label>
              <input
                type="text"
                id="contactPerson"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleChange}
                required
                placeholder="Laura Gómez"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="contacto@ejemplo.com"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="phone">Teléfono *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="+34 912 345 678"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="address">Dirección *</label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                placeholder="Av. de la Playa 12"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="province">Provincia *</label>
              <select
                id="province"
                name="province"
                value={formData.province}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    province: e.target.value,
                    city: "",
                  }));
                }}
                required
              >
                <option value="">Seleccionar provincia</option>
                {provinces.map((prov) => (
                  <option key={prov.id} value={String(prov.id)}>
                    {prov.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="city">Ciudad *</label>
              <select
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                disabled={!formData.province}
              >
                <option value="">
                  {formData.province
                    ? "Seleccionar ciudad"
                    : "Selecciona primero una provincia"}
                </option>
                {cities
                  .filter((c) => c.province === Number(formData.province))
                  .map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="postalCode">Código Postal *</label>
              <input
                type="text"
                id="postalCode"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                required
                placeholder="28001"
                maxLength={5}
              />
            </div>

            {/* Campo contraseña — solo al crear un cliente nuevo */}
            {!client && (
              <div className={styles.formGroup}>
                <label htmlFor="password">Contraseña Temporal *</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={(formData as { password?: string }).password ?? ""}
                  onChange={handleChange}
                  required
                  placeholder="Mínimo 8 caracteres"
                  minLength={8}
                />
              </div>
            )}
          </div>

          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
            >
              Cancelar
            </button>
            <button type="submit" className={styles.saveButton}>
              <Icon name="checkCircle" size={18} />
              {client ? "Guardar Cambios" : "Crear Cliente"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
